import { SearchOutlined } from '@ant-design/icons';
import { TermFilter } from '@dofleini/query-builder';
import { Checkbox, Form, Input } from 'antd';
import classNames from 'classnames';
import compact from 'lodash/compact';
import find from 'lodash/find';
import compose from 'lodash/fp/compose';
import filterFP from 'lodash/fp/filter';
import joinFP from 'lodash/fp/join';
import mapFP from 'lodash/fp/map';
import reduceFP from 'lodash/fp/reduce';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';

import FilterContent from '@/components/TableFilters/components/FilterContent';
import findMatch from '@/utils/findMatch';
import { getProvinceByAcronym, provinceOptions } from '@/utils/location';

const saveTemp = (key) => (value) => localStorage.setItem(key, JSON.stringify(value));

const getTemp = (key) => JSON.parse(localStorage.getItem(key));

export const ProvinceEnumFilterComponent = ({ filter, t, applyFilter }) => {
  const [searchText, setSearchText] = useState('');

  const filteredOptions = useMemo(() => compose(
    filterFP(({ label }) => findMatch(searchText, label)),
    mapFP(({ label, ...rest }) => ({ label: t(label), ...rest })),
  )(provinceOptions), [searchText, t]);

  const onFinish = useCallback(({ value: values }) => {
    compose(
      saveTemp(`filter-${filter?.name}`),
      reduceFP((acc, value) => ({
        ...acc,
        [value]: find(filteredOptions, { value })?.label
      }), {}),
    )(values);

    let filterOptions = map(values, value => {
      if (!Array.isArray(filter.field)) {
        return new TermFilter({
          field: filter.field,
          value: value === -1 ? null : value
        }).toQuery();
      } else {
        return {
          'type': 'OR',
          'filters': filter.field?.map((e) => new TermFilter({
            field: e,
            value: value === -1 ? null : value
          }).toQuery())
        };
      }
    });

    applyFilter(
      {
        'type': 'OR',
        'filters': filterOptions
      }
    );
  }, [applyFilter, filter.field, filter?.name, filteredOptions]);

  return (
    <>
      {
        filter.search &&
        <div className="p-2">
          <Input suffix={<SearchOutlined/>} value={searchText} onChange={({ target }) => setSearchText(target?.value)}/>
        </div>
      }
      <Form onFinish={onFinish}>
        <FilterContent t={t}>
          <Form.Item name="value" className="m-0">
            <Checkbox.Group className="w-full">
              <div className="w-full flex flex-col max-w-300px max-h-300px overflow-y-auto">
                {
                  filteredOptions.map(({ value, label }) => (
                    <Checkbox className={classNames('mx-0 my-px p-2')} key={value} value={value}>
                      {label}
                    </Checkbox>
                  ))
                }
              </div>
            </Checkbox.Group>
          </Form.Item>
        </FilterContent>
      </Form>
    </>
  );
};

ProvinceEnumFilterComponent.propTypes = {
  filter: PropTypes.object,
  t: PropTypes.func,
  applyFilter: PropTypes.func,
};

export const ProvinceValueEnumFilterComponent = ({ filterValue, filter }) => {
  const mapOptions = getTemp(`filter-${filter?.name}`);
  const items = useMemo(() => filterValue ? (mapOptions || {}) : {}, [filterValue, mapOptions]);

  const options = useMemo(() => compose(
    joinFP(', '),
    compact,
    uniq,
    mapFP(province => province?.name),
    mapFP(element => getProvinceByAcronym(element?.value || element)),
  )(Object.keys(items)), [items]);

  return <p className="m-0 break-world leading-snug whitespace-pre-wrap">{options}</p>;
};

ProvinceValueEnumFilterComponent.propTypes = {
  filter: PropTypes.object,
  t: PropTypes.func,
  filterValue: PropTypes.any,
};
